import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18next, { changeLanguageSafe } from "@/i18n";
import { api } from "@/api";

const StatusBadge = ({ ok, t }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${ok ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
    {ok ? t("status.ok") : t("status.offline")}
  </span>
);

const Card = ({ title, children }) => (
  <div className="border rounded-md p-4 bg-background shadow-sm">
    <h3 className="text-sm font-medium text-foreground mb-2">{title}</h3>
    {children}
  </div>
);

export default function StatusPage() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState(i18next.language || "de");

  async function fetchCloudflareDirect() {
    try {
      const t0 = Date.now();
      const resp = await fetch("https://www.cloudflarestatus.com/api/v2/status.json");
      const json = await resp.json();
      const ok = !!json?.status && json.status.indicator === "none";
      const description = json?.status?.description || null;
      const pingMs = Date.now() - t0;
      return { ok, description, pingMs };
    } catch (e) {
      return { ok: false, error: e?.message || t("status.cloudflareFetchError") };
    }
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.systemStatus();
      setData(res);
    } catch (e) {
      setError(e?.message || t("status.fetchError"));
      // Backend is offline; still try Cloudflare directly as fallback
      try {
        const cf = await fetchCloudflareDirect();
        setData({ cloudflare: cf });
      } catch (_) {
        setData(null);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function changeLanguage(l) {
    setLang(l);
    await changeLanguageSafe(l);
  }

  const backendOk = !!data?.backend?.ok && !error;
  const dbOk = !!data?.db?.ok && backendOk;
  const smtpOk = !!data?.smtp?.ok && backendOk;
  const cfOk = !!data?.cloudflare?.ok; // decoupled from backend

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">{t("status.title")}</h2>
        <button
          onClick={load}
          className="text-sm px-3 py-1.5 rounded border hover:bg-muted"
        >{t("status.refresh")}</button>
        <div className="ml-2 flex items-center gap-2">
          <label className="text-xs text-muted-foreground" htmlFor="lang">{t("language.label")}</label>
          <select
            id="lang"
            className="text-sm border rounded px-2 py-1"
            value={lang}
            onChange={(e) => changeLanguage(e.target.value)}
          >
            <option value="de">{t("language.de")}</option>
            <option value="en">{t("language.en")}</option>
            <option value="es">{t("language.es")}</option>
          </select>
        </div>
      </div>

      {loading && (
        <div className="text-sm text-muted-foreground mb-4">{t("status.loading")}</div>
      )}
      {error && (
        <div className="text-sm text-red-600 mb-4">{error}</div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card title={t("status.backend")}>
          <div className="flex items-center justify-between mb-1">
            <StatusBadge ok={backendOk} t={t} />
            {data?.backend?.uptimeSec != null && (
              <span className="text-xs text-muted-foreground">{t("status.uptime", { sec: data.backend.uptimeSec })}</span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">{t("status.time", { time: data?.backend?.now || "—" })}</div>
        </Card>

        <Card title={t("status.db")}>
          <div className="flex items-center justify-between mb-1">
            <StatusBadge ok={dbOk} t={t} />
            <span className="text-xs text-muted-foreground">{data?.db?.pingMs != null ? t("status.ping", { ms: data.db.pingMs }) : "—"}</span>
          </div>
          {!dbOk && (
            <div className="text-xs text-muted-foreground">{data?.db?.error || (backendOk ? t("status.error") : t("status.offline"))}</div>
          )}
        </Card>

        <Card title={t("status.smtp")}>
          <div className="flex items-center justify-between mb-1">
            <StatusBadge ok={smtpOk} t={t} />
            <span className="text-xs text-muted-foreground">{data?.smtp?.pingMs != null ? t("status.ping", { ms: data.smtp.pingMs }) : "—"}</span>
          </div>
          <div className="text-xs text-muted-foreground">{t("status.source", { source: data?.smtp?.source || "—" })}</div>
          {!smtpOk && (
            <div className="text-xs text-muted-foreground">{data?.smtp?.error || (backendOk ? t("status.error") : t("status.offline"))}</div>
          )}
        </Card>

        <Card title={t("status.cloudflare")}>
          <div className="flex items-center justify-between mb-1">
            <StatusBadge ok={cfOk} t={t} />
            <span className="text-xs text-muted-foreground">{data?.cloudflare?.pingMs != null ? t("status.ping", { ms: data.cloudflare.pingMs }) : "—"}</span>
          </div>
          <div className="text-xs text-muted-foreground">{data?.cloudflare?.description || "—"}</div>
          {!cfOk && (
            <div className="text-xs text-muted-foreground">{data?.cloudflare?.error || t("status.error")}</div>
          )}
        </Card>
      </div>
    </div>
  );
}
